import React, {useContext, useEffect} from 'react';

import {DeferredFallbacksContext, IsServerResolved, markServerDataHydrated} from 'coreHooks';

import PageInitSafetyNet from './PageInitSafetyNet';

interface SSRPageComponent<P = Record<string, unknown>> extends React.FC<P> {
	getTemplateName: () => string;
	useReactQuery: boolean;
}

const DeferredFallbacks = () => {
	const fallbacks = useContext(DeferredFallbacksContext);

	if (fallbacks.length === 0) {
		return null;
	}

	return <>{fallbacks.map((Fallback, i) => <Fallback key={i} />)}</>;
};

/**
 * HOC that attaches the static properties the routing framework expects
 * (`getTemplateName`, `useReactQuery`).
 *
 * Suspension is handled by the stable outer `<Suspense>` boundary in
 * `StrictLocaleLoaderRouter`, which — combined with `startTransition` —
 * keeps the previous page visible during CSR navigation until data resolves.
 *
 * `DeferredFallbacks` renders **after** `Content` inside the same Suspense
 * boundary. When `Content` suspends (e.g. via `useServerData`), the entire
 * boundary falls back — so fallbacks only run once the page has had a chance
 * to provide its own values via `useProvideDeferredData`.
 *
 * Route, SEO, and related deferreds are normally set by
 * `usePageInit`. `PageInitSafetyNet` runs only after `Content` stops suspending
 * and fills any missing route/SEO state for pages that skipped those calls.
 *
 * Optional `statics` param forwards additional properties (e.g. compound
 * components) onto the returned component so callers avoid manual boilerplate.
 */
const withSSRPage = <
		P extends Record<string, unknown> = Record<string, unknown>,
		S extends Record<string, unknown> = Record<string, never>,
	>(
		Content: React.ComponentType<P>,
		templateName: string,
		statics?: S,
	): SSRPageComponent<P> & S => {
	const Page = (props: P) => {
		// Runs once the page boundary has hydrated, after every query of the page has mounted.
		useEffect(() => {
			markServerDataHydrated();
		}, []);

		return (
			<>
				<Content {...props} />
				<IsServerResolved />
				<PageInitSafetyNet templateName={templateName} />
				<DeferredFallbacks />
			</>
		);
	};

	Page.displayName = `withSSRPage(${Content.displayName || Content.name || templateName})`;
	Page.getTemplateName = () => templateName;
	Page.useReactQuery = true;

	if (statics) {
		Object.assign(Page, statics);
	}

	return Page as unknown as SSRPageComponent<P> & S;
};

export default withSSRPage;
